import columnizedView from './columnizedComponent.html';
import './columnizedComponent.css';
import _ from 'lodash';
import { ensure } from 'components/sharpr/ensure/ensure';

export function columnizedComponent() {
	return {
		bindings: {
			minColumnWidth: '<',
			maxColumnWidth: '<',
			gutterWidth: '<',
			renderDelay: '<?',
			pins: '<?',
		},
		transclude: true,
		controllerAs: 'vm',
		controller: columnizedComponentCtrl,
		template: columnizedView,
	};
}

let defaultPinId = 0;

export class columnizedComponentCtrl {
	constructor($scope, $element, ImageSizer) {
		'ngInject';
		this.$scope = $scope;
		this.$element = $element;
		this.ImageSizer = ImageSizer;
		this.renderCount = 0;
		this.identityByPinId = {};
		// self-bind some handlers
		this.handleWindowResize = this.handleWindowResize.bind(this);
		this.checkForMoreLessButtons = this.checkForMoreLessButtons.bind(this);
		this.layoutOnChanges = this.layoutOnChanges.bind(this);
		this.handleMobileImageLoad = this.handleMobileImageLoad.bind(this);
		this.hideUntilLayout = this.hideUntilLayout.bind(this);
	}

	$onInit() {
		// default the render delay to 200ms
		this.throttleMs = this.renderDelay || 200;
		// adjust render delay for IE 11
		if (navigator.userAgent.match(/MSIE/)) {
			// IE is so much less capable, we have to throttle at longer interval
			this.throttleMs *= 2.5;
		}
		// get element
		this.container = this.$element[0].firstChild; // our empty <div ng-transclude></div>
		// avoid an initial flash of content by hiding content for a short time
		this.container.style.visibility = 'hidden';
		// create a throttled version of the layout function
		this.throttledLayout = _.throttle(() => this.layout(), this.throttleMs, {
			leading: true,
			trailing: true,
		});
		// handle changes
		angular.element(window).on('resize', this.handleWindowResize);
		this.currentViewportWidth = window.innerWidth;
		if (this.pins) {
			this.$scope.$watch(() => (this.pins || []).length, this.hideUntilLayout);
			this.layoutInterval = setInterval(this.layoutOnChanges, this.throttleMs);
			angular.element(this.container).on('click', this.checkForMoreLessButtons);
		} else {
			setTimeout(() => this.throttledLayout(), this.throttleMs);
		}
		if (window.isMobile) {
			this.setupImageLoadHandler();
		}
	}

	$onDestroy() {
		angular.element(window).off('resize', this.handleWindowResize);
		angular.element(this.container).off('click', this.checkForMoreLessButtons);
		this.container.removeEventListener('load', this.handleMobileImageLoad, true);
		clearInterval(this.layoutInterval);
	}

	hideUntilLayout(newNumPins, oldNumPins) {
		if (oldNumPins == 0 && newNumPins > 0) {
			this.container.style.visibility = 'hidden';
		} else {
			this.$element.css('height', '0');
		}
	}

	checkForMoreLessButtons(evt) {
		if (evt.target && evt.target.classList.contains('post-view-more-less')) {
			let node = evt.target;
			while ((node = node.parentNode)) {
				if (node.parentNode === this.container) {
					node.columnizedHeight = null;
					// let post expand or contract so we can measure correctly
					setTimeout(() => this.layout(), 0);
					break;
				}
			}
		}
	}

	handleWindowResize() {
		const containerWidth = Math.round(this.container.clientWidth);
		if (containerWidth == this.currentContainerWidth) {
			// window width didn't change
			// console.log('window width didn\'t change');
			return;
		}
		this.currentContainerWidth = containerWidth;
		if (window.isMobile) {
			// on mobile, the width depends on how many posts fit across the viewport
			_.forEach(this.container.children, child => (child.columnizedHeight = null));
		}
		this.throttledLayout();
	}

	// Times we need to layout:
	// 1. Initial set up
	// 2. Posts added/removed (delete action, inifinite scroll)
	// 3. Search criteria changed (this.pins will be a new Array object)
	// 4. "More"/"Less" button clicked
	// 5. Post edited
	// 6. Window Resize
	// Times where post height needs calculation
	// 1. Initial set up (all items)
	// 2. "More"/"Less" button clicked (the item clicked)
	// 3. Post edited (the one edited)
	// 4. Window Resize on Mobile (all items)
	layoutOnChanges() {
		const identity = ensure.array(this.pins).map(this._getPinIdentity).join('|');
		if (this.lastIdentity === identity) {
			// no changes
			return;
		}
		// force re-calculation of height
		[...this.container.children].forEach(child => (child.columnizedHeight = null));
		// store new identity
		this.lastIdentity = identity;
		// re-layout
		this.layout();
	}

	_getPinIdentity(pin) {
		if (!pin) {
			return defaultPinId++;
		}
		return (
			pin.modified_at +
			pin.deleted_at +
			pin.is_client_intel +
			pin.intel_text +
			ensure.array(pin.attachment_thumbnails).length +
			ensure.object(pin.image).url +
			(pin.percent >= 0) +
			pin.summaryComplete +
			pin.is_pinned
		);
	}

	setupImageLoadHandler() {
		this.container.addEventListener('load', this.handleMobileImageLoad, true);
	}

	handleMobileImageLoad(evt) {
		// image has loaded; traverse up tree until we find columnized item
		// then clear out columnizedHeight so that layout will re-run it
		let node = evt.target;
		while ((node = node.parentNode)) {
			if (node.parentNode && node.parentNode.parentNode === this.container) {
				node.parentNode.columnizedHeight = null;
				this.throttledLayout();
				break;
			}
		}
	}

	layout() {
		// let start = +new Date;
		if (this.isLayingOut) {
			// console.log('<sh-columnized> already laying out!');
			// already laying out; wait at least until layout is finished
			setTimeout(() => this.throttledLayout(), this.throttleMs);
			return;
		}
		this.isLayingOut = true;
		this.renderCount++;
		this.container.style.visibility = 'hidden';
		// our boatload of variables
		let remainder;
		let children;
		let containerWidth;
		let childWidth;
		let numColumns;
		let stretchBy;
		let finalChildWidth;
		let columnOffset;
		let child;
		let height;
		let thisColumn;
		let nextColumn;
		let thresholdPx;
		let skipThreshold = 0.66;
		let columns = [];
		let colIdx = 0;
		let highest = 0;
		// get our children, ignoring text nodes
		children = _.filter(this.container.children, n => n.nodeType == 1);
		if (children.length === 0) {
			// nothing to lay out
			this.$element[0].style.height = '0';
			this.isLayingOut = false;
			return;
		}
		// get the current width of our container
		containerWidth = this.currentContainerWidth || Math.floor(this.container.clientWidth);
		// childWidth starts out as the min width
		childWidth = this.minColumnWidth;
		// divide to get the number of columns, taking gutter width into account
		numColumns = Math.floor((containerWidth + this.gutterWidth) / (childWidth + this.gutterWidth));
		if (numColumns === 0) {
			// always must have at least one column
			numColumns = 1;
			remainder = 0;
		} else if (numColumns > children.length) {
			// the children don't fill the width
			numColumns = children.length;
			// so we need to see how much we could potentially stretch them
			remainder = containerWidth + this.gutterWidth - (childWidth + this.gutterWidth) * numColumns;
		} else {
			// the children span the columns but the columns can be widened a bit
			// to avoid extra space on the right side
			remainder = (containerWidth + this.gutterWidth) % (childWidth + this.gutterWidth);
		}
		// the width we can stretch each item
		stretchBy = Math.floor(remainder / numColumns);
		// our final width
		finalChildWidth = this.minColumnWidth + stretchBy;
		if (finalChildWidth > this.maxColumnWidth) {
			// unless it is greater than the max, then constrain it
			finalChildWidth = this.maxColumnWidth;
		}
		// final width plus gutter width is the x offset from one column to the next
		columnOffset = finalChildWidth + this.gutterWidth;

		// set width of all child elements so they will layout and we can get effective height
		// console.log('finalChildWidth=', finalChildWidth);
		children.forEach(child => (child.style.width = finalChildWidth + 'px'));
		// create an array of columns that holds the x offset of the column
		// and the total height of all the items in that column
		for (let i = 0; i < numColumns; i++) {
			columns.push({
				left: columnOffset * i,
				height: 0,
			});
		}
		// function to help handle column incrementing and wrapping
		function getThisColumn() {
			return columns[colIdx];
		}
		function getNextColumn() {
			return columns[colIdx + 1 === numColumns ? 0 : colIdx + 1];
		}
		function incrementColumn(num) {
			colIdx += num;
			if (colIdx >= numColumns) {
				colIdx = 0;
			}
		}
		// run through each child to set width and offset
		for (let i = 0, len = children.length; i < len; i++) {
			child = children[i];
			if (this.pins && window.isMobile) {
				this._setPinImgSize(child, i, finalChildWidth);
			}
			// get the effective height of the child, rounding to nearest pixel
			// height = Math.round(child.getBoundingClientRect().height);
			// console.count('child.getBoundingClientRect().height');
			if (!child.columnizedHeight) {
				child.columnizedHeight = Math.round(child.clientHeight);
				// console.log('ran child.clientHeight');
			}
			height = child.columnizedHeight;
			//  get the threshold for the next child to be stacked under here
			thresholdPx = Math.round(height * skipThreshold);
			// get the column specs
			thisColumn = getThisColumn();
			nextColumn = getNextColumn();
			// find the next shortest column
			while (nextColumn.height > 0 && thisColumn.height - thresholdPx > nextColumn.height) {
				thisColumn = nextColumn;
				incrementColumn(1);
				nextColumn = getNextColumn();
			}
			// set the x,y offset using transform translate
			child.style.transform = `translate3d(${thisColumn.left}px, ${thisColumn.height}px, 0)`;
			// recalculate the total width of the column
			thisColumn.height += height + this.gutterWidth;
			// keep track of which column is the longest
			if (thisColumn.height > highest) {
				highest = thisColumn.height;
			}
			// stack into this column again if previous post is way long
			if (nextColumn.height - thresholdPx < thisColumn.height) {
				incrementColumn(1);
			}
		}
		this.container.style.visibility = 'visible';
		this.$element[0].style.height = highest + 'px';
		this.isLayingOut = false;
		// console.log('layout took ' + (+new Date - start) + 'ms');
	}

	_setPinImgSize(element, i, boundingWidth) {
		if (!this.pins) {
			return;
		}
		let post = this.pins[i];
		if (!post || !post.image || !post.image.width) {
			return;
		}
		let img = element.querySelector('img');
		if (!img) {
			return;
		}
		let size = this.ImageSizer.pinSize(post.image, { boundingWidth });
		if (!size || !size[0] || !size[1]) {
			return;
		}
		img.width = size[0];
		img.height = size[1];
	}
}
